import { Fragment, type ReactNode } from 'react';

/**
 * Minimal markdown renderer for the Guides content.
 *
 * The guides are our own static files with a known, narrow structure
 * (headings, paragraphs, lists, blockquotes, bold and italic), so a small
 * hand renderer beats shipping react-markdown for content we control.
 * Anything the guides start using must be added here deliberately.
 */

function renderInline(text: string): ReactNode {
  // **bold** and *italic*; process bold first so nested asterisks resolve.
  const parts: ReactNode[] = [];
  const re = /(\*\*([^*]+)\*\*|\*([^*]+)\*)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    if (m[2] !== undefined) parts.push(<strong key={key++}>{m[2]}</strong>);
    else parts.push(<em key={key++}>{m[3]}</em>);
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length === 1 ? parts[0] : parts;
}

export function renderGuideMarkdown(md: string): ReactNode {
  const lines = md.replace(/\r\n/g, '\n').split('\n');
  const blocks: ReactNode[] = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.trim() === '') { i++; continue; }

    if (line.startsWith('### ')) {
      blocks.push(<h3 key={key++}>{renderInline(line.slice(4))}</h3>); i++; continue;
    }
    if (line.startsWith('## ')) {
      blocks.push(<h2 key={key++}>{renderInline(line.slice(3))}</h2>); i++; continue;
    }
    if (line.startsWith('# ')) {
      blocks.push(<h1 key={key++}>{renderInline(line.slice(2))}</h1>); i++; continue;
    }
    if (line.startsWith('> ')) {
      const quote: string[] = [];
      while (i < lines.length && lines[i].startsWith('> ')) { quote.push(lines[i].slice(2)); i++; }
      blocks.push(<blockquote key={key++}>{renderInline(quote.join(' '))}</blockquote>);
      continue;
    }
    if (/^(-|\*)\s/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^(-|\*)\s/.test(lines[i])) { items.push(lines[i].replace(/^(-|\*)\s+/, '')); i++; }
      blocks.push(<ul key={key++}>{items.map((it, j) => <li key={j}>{renderInline(it)}</li>)}</ul>);
      continue;
    }
    if (/^\d+\.\s/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) { items.push(lines[i].replace(/^\d+\.\s+/, '')); i++; }
      blocks.push(<ol key={key++}>{items.map((it, j) => <li key={j}>{renderInline(it)}</li>)}</ol>);
      continue;
    }
    if (/^(---|\*\*\*)\s*$/.test(line)) {
      blocks.push(<hr key={key++} />); i++; continue;
    }

    // Paragraph: consume until a blank line or a structural line.
    const para: string[] = [];
    while (
      i < lines.length && lines[i].trim() !== '' &&
      !/^(#{1,3}\s|>\s|(-|\*)\s|\d+\.\s|---\s*$)/.test(lines[i])
    ) { para.push(lines[i]); i++; }
    blocks.push(<p key={key++}>{renderInline(para.join(' '))}</p>);
  }

  return <Fragment>{blocks}</Fragment>;
}
